import { Check, Plus, X } from '@tamagui/lucide-icons';
import { noop, useQueryClient } from '@tanstack/react-query';
import { ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { Button } from '@unistocks/components/button/button';
import { FloatingButton } from '@unistocks/components/button/floating-action-button';
import { Row } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { ListRenderItem, ListRenderItemInfo } from '@unistocks/components/virtual-list/list';
import { DashboardGridProps } from '@unistocks/features/dashboard/dashboard-grid-props';
import { useItemLayout } from '@unistocks/features/dashboard/hooks/use-item-layout';
import { useNumColumns } from '@unistocks/features/dashboard/hooks/use-num-columns';
import { getDashboardItemDescriptor } from '@unistocks/features/dashboard/widget-registry';
import { DashboardItemDto } from '@unistocks/proxy/axios-client';
import {
  dashboardGetQueryKey,
  setDashboardGetData,
  useDashboardGetQuery,
  useDashboardSetDashboardItemOrderMutation
} from '@unistocks/proxy/axios-client/DashboardQuery';
import { useTabBarStyle } from '@unistocks/shared/hooks/use-tab-bar-style';
import { t } from '@unistocks/shared/localization/index.ts';
import { triggerHaptics } from '@unistocks/shared/utils/haptics/haptics';
import { extractId } from '@unistocks/shared/utils/list-utils';
import { useNavigation } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Animated, { useAnimatedRef } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Sortable, {
  SortableGridDragEndCallback,
  SortableGridRenderItem
} from 'react-native-sortables';
import { getVariableValue, Heading, View } from 'tamagui';

/**
 * Dashboard grid rendered as a FlashList, with multiple columns on large screens
 * and a drag-to-sort mode. Shared by native screens and small web screens.
 */
export const DashboardList: React.FC<DashboardGridProps> = ({
  openSettings,
  addItemOpen,
  openAddItem
}) => {
  const queryClient = useQueryClient();
  const navigation = useNavigation();
  const { bottom } = useSafeAreaInsets();
  const scrollRef = useAnimatedRef<Animated.ScrollView>();
  const sortOrder = useRef<string[]>([]);
  const [sortActive, setSortActive] = useState(false);
  const tabBarStyle = useTabBarStyle();
  const numColumns = useNumColumns();
  const space3 = getVariableValue('$3', 'space');

  const extra = {
    openSettings,
    setSortActive
  };

  const {
    data: dashboard = [],
    refetch,
    isRefetching
  } = useDashboardGetQuery({
    select: data => data?.items ?? []
  });

  const { mutate: setDashboardItemOrder } = useDashboardSetDashboardItemOrderMutation({
    onMutate: newOrder => {
      setDashboardGetData(queryClient, data => {
        const newData = [...(data?.items ?? [])].sort((a, b) => {
          const aIndex = newOrder.indexOf(a.id);
          const bIndex = newOrder.indexOf(b.id);

          return aIndex - bIndex;
        });

        return { items: newData };
      });
    },
    onSuccess: res => {
      setDashboardGetData(queryClient, () => res);
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: dashboardGetQueryKey() });
    }
  });

  useEffect(() => {
    if (sortActive) {
      navigation.setOptions({
        tabBarStyle: {
          display: 'none'
        }
      });

      return () => {
        // Reapply the original tab bar styles (set in the tab navigator)
        navigation.setOptions({ tabBarStyle });
      };
    }
  }, [navigation, sortActive, tabBarStyle]);

  const invalidateDashboardQueries = useCallback(() => {
    refetch();
    dashboard.forEach(({ id }) => {
      queryClient.invalidateQueries({ predicate: query => query.queryKey.includes(id) });
    });
  }, [dashboard, queryClient, refetch]);

  const onSaveSortPress = () => {
    setSortActive(false);
    setDashboardItemOrder(sortOrder.current);
    sortOrder.current = [];
  };

  const onDragEnd = useCallback<SortableGridDragEndCallback<DashboardItemDto>>(({ data }) => {
    sortOrder.current = data.map(({ id }) => id);
  }, []);

  const renderSortableList = () => {
    return (
      <>
        <Page.ScrollView
          ref={scrollRef}
          headerBackArrow={false}
          headerTitle={
            <Heading mx={'auto'} size={'$6'} textAlign="center">
              {t('::Dashboard:SortItems')}
            </Heading>
          }
        >
          <Sortable.Grid
            scrollableRef={scrollRef}
            data={dashboard}
            keyExtractor={extractId}
            rowGap={space3}
            hapticsEnabled
            enableActiveItemSnap={false}
            activeItemScale={0.9}
            showDropIndicator
            renderItem={renderSortableItem}
            onDragEnd={onDragEnd}
          />
        </Page.ScrollView>

        <Row bg={'$background'} gap={'$2'} bottom={bottom}>
          <Button
            size={'$4'}
            variant="outlined"
            Icon={X}
            flex={1}
            onPress={() => setSortActive(false)}
          >
            {t('::Cancel')}
          </Button>
          <Button size={'$4'} Icon={Check} flex={1} onPress={onSaveSortPress}>
            {t('::Save')}
          </Button>
        </Row>
      </>
    );
  };

  const renderDashboard = () => {
    return (
      <Page.VirtualList
        masonry={numColumns > 1}
        numColumns={numColumns}
        data={dashboard}
        extraData={extra}
        keyExtractor={extractId}
        renderItem={renderDashboardItem}
        ItemSeparatorComponent={Separator}
        onRefresh={invalidateDashboardQueries}
        refreshing={isRefetching}
        contentContainerStyle={ScrollViewStyles.contentContainerStyle}
        headerBackArrow={false}
      />
    );
  };

  return (
    <>
      {sortActive ? renderSortableList() : renderDashboard()}
      <FloatingButton
        display={sortActive ? 'none' : undefined}
        Icon={Plus}
        open={addItemOpen}
        onPress={openAddItem}
      />
    </>
  );
};

DashboardList.displayName = 'DashboardList';

const renderDashboardItem: ListRenderItem<DashboardItemDto> = props => (
  <DashboardItemWrapper {...props} />
);

const renderSortableItem: SortableGridRenderItem<DashboardItemDto> = ({ item }) => {
  const DashboardItem = getDashboardItemDescriptor(item.type).component;

  return <DashboardItem item={item} openSettings={noop} />;
};

const DashboardItemWrapper: React.FC<ListRenderItemInfo<DashboardItemDto>> = ({
  item,
  extraData: { openSettings, setSortActive },
  index
}) => {
  const DashboardItem = getDashboardItemDescriptor(item.type).component;
  const numColumns = useNumColumns();
  const itemLayout = useItemLayout(index);

  return (
    <DashboardItem
      item={item}
      itemLayout={itemLayout}
      my={numColumns > 1 ? '$1' : undefined}
      mx={numColumns > 1 ? '$2' : undefined}
      openSettings={openSettings}
      onLongPress={() => {
        triggerHaptics('longPress');
        setSortActive(true);
      }}
    />
  );
};

const Separator: React.FC = () => <View my={'$2'} />;
Separator.displayName = 'Separator';
